import type { CSSProperties } from "react";
import { COULEURS, POLICE_TITRE_SPLASH, POLICE_UI_SPLASH } from "../splashStyles.js";

/**
 * ============================================================================
 *  guideStyles.ts — Les valeurs du handoff « visite guidée », en un seul endroit
 * ----------------------------------------------------------------------------
 *  Tokens Écrin (`splashStyles`) et verre dépoli de `SceneNav` / `VariantSwitcher` :
 *  mêmes fond, flou et bordure, pour que le guide ne soit pas une autre application.
 * ==========================================================================*/

export const BANDE = "#0B1310";

/**
 * Hauteurs des bandes (hors zone sûre) : haute / basse, ordinateur et téléphone. La bande
 * haute est agrandie de 20 % par rapport au handoff (76 → 91 px, 84 → 101 px) ; la basse est
 * celle du handoff.
 */
export const HAUTEURS = {
  ordinateur: { haut: 91, bas: 136 },
  telephone: { haut: 101, bas: 196 },
} as const;

/** Transition de hauteur des bandes ; le contenu, lui, passe en fondu (.4 s). */
export const COURBE = "cubic-bezier(.65,0,.35,1)";
export const DUREE_BANDES = ".8s";

export const SAUGE = COULEURS.sauge;
export const CREME = COULEURS.creme;
export const AVENIR = "rgba(246,238,227,.3)";

/** Verre dépoli des panneaux (même recette que la navigation et les ambiances). */
export const verre: CSSProperties = {
  background: "rgba(16,26,23,.74)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  border: "1px solid rgba(246,238,227,.22)",
  borderRadius: 2,
  color: CREME,
  fontFamily: POLICE_UI_SPLASH,
};

export const surtitre: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontFamily: POLICE_UI_SPLASH,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: ".16em",
  textTransform: "uppercase",
  color: "#E3D9C8",
};

export const titreItalien = (taille: number): CSSProperties => ({
  fontFamily: POLICE_TITRE_SPLASH,
  fontSize: taille,
  lineHeight: 1.15,
  color: CREME,
  fontWeight: 400,
});

export const ellipse: CSSProperties = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

/** Bouton rond (cible ≥ 44 px). */
export const rond = (taille: number, plein = false): CSSProperties => ({
  appearance: "none",
  width: taille,
  height: taille,
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  cursor: "pointer",
  flex: "none",
  border: plein ? "none" : "1px solid rgba(246,238,227,.4)",
  background: plein ? CREME : "transparent",
  color: plein ? COULEURS.vert : CREME,
});

/** Bouton carré (son, sous-titres) : angles de 4 px, plein crème quand il est actif. */
export const carre = (taille: number, actif = false): CSSProperties => ({
  appearance: "none",
  width: taille,
  height: taille,
  borderRadius: 4,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  cursor: "pointer",
  flex: "none",
  border: actif ? "1px solid " + CREME : "1px solid rgba(246,238,227,.3)",
  background: actif ? CREME : "transparent",
  color: actif ? COULEURS.vert : CREME,
});

/** Bouton à contour (« Quitter le guide », « Chapitre précédent »…). */
export const contour: CSSProperties = {
  appearance: "none",
  minHeight: 44,
  padding: "0 18px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  background: "transparent",
  color: CREME,
  border: "1px solid rgba(246,238,227,.4)",
  borderRadius: 2,
  fontFamily: POLICE_UI_SPLASH,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: ".04em",
  cursor: "pointer",
};

/** Bouton principal crème. */
export const plein: CSSProperties = {
  ...contour,
  background: CREME,
  color: COULEURS.vert,
  border: "1px solid " + CREME,
};

export const desactive: CSSProperties = { opacity: 0.4, cursor: "default" };

export { COULEURS, POLICE_TITRE_SPLASH, POLICE_UI_SPLASH };
